import React, { useRef, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Button, FormControl } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'

import { MAX_FILE_SIZE, MAX_PDF_SIZE, DROPZONE_ACCEPTED_FILES_PRO, MAX_FILE_SIZE_FREE } from '../../constants'
import {
  createFileInput,
  getErrorMessage,
  isPDF,
  isTotalSizeWithinLimit,
  isValidFileSize,
  isPhpFile
} from '../../utils/general'
import ContentSelector from '../ContentSelector/ContentSelector'
import SelectedFile from '../SelectedFile/SelectedFile'
import { transferRequestLinkUpdate } from '../../services/site'
import { isZipFile } from '../CreateUpdateSiteModal/interface'
import HomePageHeader from '../HomePageHeader'
import '../../Landing/Home/Home.css'
import { LoadingBox } from '../LoadingBox/LoadingBox'
import { ErrorBox } from '../ErrorBox/ErrorBox'
import { calculateTotalSize, MAX_DIRECT_UPLOAD_SIZE } from '../../services/create-update-site/pro'
import { isVideoFile } from '../../helpers/file-type'
import { handlePresignedUploads, getFilesMetadata } from '../../services/presigned-uploads/handlePresignedUploads'
import Footer from '../Footer'
import StyledDropzone from '../StyledDropzone'

const schema = yup.object({
  email: yup.string().email("Oops, that's not a valid email").required('Please enter an email'),
  fileSelected: yup.mixed().required('Please select a file')
})

const TransferRequestLinkUpload = () => {
  const { domain } = useParams()
  const formRef = useRef()
  const [showSuccess, setShowSuccess] = useState(false)
  const [showLoading, setShowLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [progress, setProgress] = useState(0)
  const [multipartUpload, setMultipartUpload] = useState(false)

  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (showLoading) {
        localStorage.setItem('cancelPresignedUpload', 'true')
        event.preventDefault()
        event.returnValue = ''
        /** Chrome requires returnValue to be set */
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  })

  const onFileSelected = async (passedFiles, setFieldValue) => {
    let files = passedFiles

    if (files.some((file) => file.path?.startsWith('/'))) {
      files = files.filter((file) => !file.path?.startsWith('/'))
    }

    if (files.some((file) => isZipFile(file.type)) && files.length > 1) {
      alert("You can't upload a zip in combination with other files. Please put all your files into the ZIP file.")
      return
    }

    const selectedFile = files[0]
    if (isPhpFile(selectedFile)) {
      alert('PHP files are not supported for transfer request links.')
      return
    }

    setFieldValue('fileSelected', selectedFile)
    setFieldValue('files', files.slice(1))
  }

  const uploadProgress = ({ loaded, total }, { size }, isMultipartUpload = false) => {
    if (isMultipartUpload) setMultipartUpload(true)

    const currentProgress = isMultipartUpload
      ? Math.round((100 * loaded) / total)
      : Math.round(((100 * loaded) / total) * 0.5)

    if ((!isMultipartUpload || loaded) && currentProgress > progress) {
      setProgress(currentProgress)
    }
  }

  const onSubmit = async ({ fileSelected, files, email }) => {
    const formData = new FormData()
    formData.append('domain', domain)
    formData.append('uploaderEmail', email.toLowerCase())

    const allFiles = files.length > 0 ? [fileSelected, ...files] : [fileSelected]

    const totalSize = await calculateTotalSize(allFiles)
    const isVideoFiles = allFiles.some((file) => isVideoFile(file.name, file.type))
    const isPresignedUpload = totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles

    setShowLoading(true)
    setProgress(0)

    try {
      if (isPresignedUpload) {
        const totalFilesMetadata = {
          totalFiles: getFilesMetadata(allFiles),
          completedFiles: []
        }

        const config = {
          domain,
          uploaderEmail: email.toLowerCase()
        }
        await handlePresignedUploads({
          files: allFiles,
          domain,
          newDomain: domain,
          formData,
          config,
          isUpdate: true,
          isUpdateSite: true,
          uploadProgress,
          isLastFile: allFiles.length === 1 ? true : false,
          filesToAddPresignedTotalSize: totalSize,
          filesToAddPresignedMetadata: totalFilesMetadata,
          isTransferRequestLink: true,
          uploaderEmail: email.toLowerCase()
        })
      } else {
        if (files.length > 0) {
          formData.append('filesToAdd', fileSelected)
          files.forEach((file) => {
            formData.append('filesToAdd', file)
          })
        } else {
          formData.append('filesToAdd', fileSelected)
        }

        await transferRequestLinkUpdate({ domain, uploaderEmail: email.toLowerCase() }, formData, (event) => {
          const { total, loaded } = event
          uploadProgress({ total, loaded }, { size: total })
        })
      }

      setShowLoading(false)
      setShowSuccess(true)
    } catch (err) {
      setShowLoading(false)
      setShowError(true)
      const errorMessage = getErrorMessage(err)
      setErrorMessage(errorMessage || 'Oops, something went wrong')
      Sentry.captureException(err, {
        tags: {
          section: 'transfer-request-link'
        }
      })
    }
  }

  const addMoreFiles = (setFieldValue, files, fileSelected) => {
    const input = createFileInput()

    requestAnimationFrame(() => {
      input.addEventListener('change', (event) => {
        const filesToAdd = Array.from(event.target.files || [])
        if (!filesToAdd.length) return

        const validFiles = []

        for (const fileToAdd of filesToAdd) {
          fileToAdd.path = fileToAdd.name

          if (
            isZipFile(fileSelected?.type) ||
            [...files, ...validFiles, fileToAdd].some((file) => isZipFile(file.type))
          ) {
            alert(
              "You can't upload a zip in combination with other files.\nPlease put all your files into the ZIP file."
            )
            return
          }

          validFiles.push(fileToAdd)
        }

        if (validFiles.length > 0) {
          const updatedFiles = [...files, ...validFiles]
          setFieldValue('files', updatedFiles)
        }
      })

      input.click()
    })
  }

  const removeFile = (setFieldValue, files, fileSelected, isSelectedFile) => {
    if (isSelectedFile && files.length > 0) {
      setFieldValue('fileSelected', files[0])
      setFieldValue('files', files.slice(1))
    } else if (isSelectedFile) {
      setFieldValue('fileSelected', undefined)
      setFieldValue('files', [])
    } else {
      const updatedFiles = files.filter((f) => f.name !== fileSelected.name)
      setFieldValue('files', updatedFiles)
    }
  }

  return (
    <div className="home">
      <HomePageHeader />
      <div className="mt-5 mb-5">
        <h1 className="font-26 bold mb-2">Upload files to</h1>
        <a
          href={`https://${domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="subtitle d-block mb-5 text-decoration-none"
        >
          {domain}
        </a>
        <div className="upload-box" style={{ maxWidth: '650px', width: '100%' }}>
          {showLoading ? (
            <LoadingBox
              message="Uploading files..."
              showProgressBar={progress > 0 && (progress < 50 || multipartUpload)}
              showSimulationProgressBar={progress >= 50 && !multipartUpload}
              progress={progress}
            />
          ) : showError ? (
            <ErrorBox
              message={errorMessage}
              setRootState={(state) => {
                setShowError(false)
                setErrorMessage('')
              }}
            />
          ) : showSuccess ? (
            <div className="success-box">
              <h3>Success</h3>
              <img className="celebrate" src={`${window.location.origin}/celebrate.gif`} alt="celebrate" />
              <div>
                <h4 className="domain">Your file has been uploaded</h4>
                <p className="mt-3">We have notified the owner of the files uploaded</p>
              </div>
            </div>
          ) : (
            <Formik
              innerRef={formRef}
              validationSchema={schema}
              onSubmit={onSubmit}
              initialValues={{
                fileSelected: undefined,
                files: [],
                email: ''
              }}
            >
              {({ handleSubmit, handleChange, values, setFieldValue, touched, errors }) => (
                <form onSubmit={handleSubmit}>
                  {values.fileSelected ? (
                    <>
                      <div className={`${values.files.length > 0 ? 'file-list' : ''}`}>
                        <SelectedFile
                          file={values.fileSelected}
                          onRemove={() => removeFile(setFieldValue, values.files, values.fileSelected, true)}
                        />
                        {values.files.map((file, index) => (
                          <SelectedFile
                            key={index}
                            file={file}
                            onRemove={() => removeFile(setFieldValue, values.files, file, false)}
                          />
                        ))}
                      </div>
                      {!isZipFile(values.fileSelected?.type) && (
                        <Button
                          className="mb-3 mt-2 ms-auto d-block"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addMoreFiles(setFieldValue, values.files, values.fileSelected)}
                        >
                          + Add more files
                        </Button>
                      )}
                      <div className="mb-3 mt-4">
                        <div className="bold font-20">Please enter your email</div>
                        <span className="light-grey font-small">So we can notify the owner who uploaded the files</span>
                      </div>
                      <div className="d-flex align-items-start gap-2">
                        <div className="flex-grow-1">
                          <FormControl
                            className="tr-landing-email-input"
                            name="email"
                            type="email"
                            placeholder="Email"
                            aria-label="Email"
                            value={values.email}
                            onChange={handleChange}
                            isInvalid={touched.email && errors.email}
                          />
                          {touched.email && errors.email && <div className="validation-error mt-2">{errors.email}</div>}
                        </div>
                        <Button type="submit" variant="outline-primary" style={{ minWidth: '150px' }}>
                          Upload Files
                        </Button>
                      </div>
                    </>
                  ) : (
                    <StyledDropzone
                      onFileSelect={(files) => onFileSelected(files, setFieldValue)}
                      acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}
                      showExamples={false}
                    />
                  )}
                </form>
              )}
            </Formik>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default TransferRequestLinkUpload
